<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center min-h-screen">
      <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <div class="text-red-600 mb-4">
          <span class="text-4xl">⚠️</span>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-2">Organisatör hittades inte</h3>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <router-link 
          to="/"
          class="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
        >
          Tillbaka till startsidan
        </router-link>
      </div>
    </div>

    <!-- Organizer Content -->
    <div v-else-if="organizer" class="max-w-7xl mx-auto">
      <!-- Organizer Header -->
      <div class="bg-white shadow-sm border-b">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div class="text-center">
            <img
              v-if="organizer.logoUrl"
              :src="organizer.logoUrl"
              :alt="`${organizer.organizerName} logotyp`"
              class="w-28 h-28 object-contain mx-auto mb-6 rounded-full bg-gray-50"
            />
            <h1 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {{ organizer.organizerName }}
            </h1>
            <p v-if="organizer.description" class="text-lg text-gray-600 mb-6 max-w-3xl mx-auto whitespace-pre-line">
              {{ organizer.description }}
            </p>
            <p v-else class="text-xl text-gray-600 mb-6">
              Evenemangsorganisatör på Biljetter.se
            </p>
            <div class="flex justify-center space-x-4 text-sm text-gray-500">
              <span>📧 {{ organizer.email }}</span>
              <span v-if="events.length > 0">🎭 {{ events.length }} evenemang</span>
            </div>
            <div v-if="organizer.facebook || organizer.twitter" class="flex justify-center gap-4 mt-5">
              <a
                v-if="organizer.facebook"
                :href="organizer.facebook"
                target="_blank"
                rel="noopener noreferrer"
                class="text-indigo-600 hover:text-indigo-800 underline"
              >
                Facebook
              </a>
              <a
                v-if="organizer.twitter"
                :href="organizer.twitter"
                target="_blank"
                rel="noopener noreferrer"
                class="text-indigo-600 hover:text-indigo-800 underline"
              >
                X / Twitter
              </a>
            </div>
            <div v-if="organizer.categories?.length" class="mt-6 flex flex-wrap justify-center gap-2">
              <span
                v-for="category in organizer.categories"
                :key="category"
                class="rounded-full bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-700"
              >
                {{ category }}
              </span>
            </div>
            <div v-if="organizer.tags?.length" class="mt-3 flex flex-wrap justify-center gap-2">
              <span
                v-for="tag in organizer.tags"
                :key="tag"
                class="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-600"
              >
                #{{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Events Section -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="mb-8">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">Evenemang av {{ organizer.organizerName }}</h2>
          <p class="text-gray-600">Upptäck alla evenemang från denna organisatör</p>
        </div>

        <!-- Events Grid -->
        <div v-if="events.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div 
            v-for="event in events" 
            :key="event._id"
            class="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
          >
            <!-- Event Image -->
            <div class="h-48 overflow-hidden">
              <img 
                v-if="event.imageUrl && event.imageUrl.startsWith('data:')" 
                :src="event.imageUrl" 
                :alt="event.title"
                class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
              <div v-else class="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <span class="text-white text-4xl">🎪</span>
              </div>
            </div>
            
            <!-- Event Content -->
            <div class="p-6">
              <h3 class="text-xl font-semibold text-gray-900 mb-2 line-clamp-1">{{ event.title }}</h3>
              <p class="text-gray-600 mb-4 line-clamp-2">{{ event.description }}</p>
              
              <!-- Event Details -->
              <div class="space-y-2 text-sm text-gray-500 mb-4">
                <div class="flex items-center gap-2">
                  <span class="text-indigo-500">📅</span>
                  <span>{{ formatDate(event.date) }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-indigo-500">📍</span>
                  <span class="line-clamp-1">{{ event.location }}</span>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="flex justify-end space-x-2">
                <router-link 
                  :to="`/events/${event._id}`"
                  class="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  Se detaljer
                </router-link>
                <router-link 
                  v-if="isOwnProfile"
                  :to="`/events/${event._id}/edit`"
                  class="bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-700 transition-colors"
                >
                  Redigera
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <div class="text-6xl mb-4">🎭</div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">Inga evenemang tillgängliga</h3>
          <p class="text-gray-600">Denna organisatör har inte skapat några evenemang än.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';

const route = useRoute();
const store = useStore();
const organizer = ref(null);
const events = ref([]);
const loading = ref(true);
const error = ref('');

const currentUser = computed(() => store.state.user);
const isOwnProfile = computed(() => {
  return currentUser.value?.organizerSlug === route.params.organizerSlug;
});

const updateSeoMetadata = () => {
  if (!organizer.value) return;

  document.title = organizer.value.seoTitle || `${organizer.value.organizerName} | Biljetter.se`;
  let descriptionMeta = document.querySelector('meta[name="description"]');
  if (!descriptionMeta) {
    descriptionMeta = document.createElement('meta');
    descriptionMeta.name = 'description';
    document.head.appendChild(descriptionMeta);
  }
  descriptionMeta.content = organizer.value.seoDescription || organizer.value.description || '';

  let keywordsMeta = document.querySelector('meta[name="keywords"]');
  if (!keywordsMeta) {
    keywordsMeta = document.createElement('meta');
    keywordsMeta.name = 'keywords';
    document.head.appendChild(keywordsMeta);
  }
  keywordsMeta.content = [...(organizer.value.categories || []), ...(organizer.value.tags || [])].join(', ');
};

watch(organizer, updateSeoMetadata);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const fetchOrganizerData = async () => {
  try {
    loading.value = true;
    error.value = '';
    
    const organizerSlug = route.params.organizerSlug;
    
    // Fetch organizer data
    const organizerResponse = await axios.get(`/api/organizers/${organizerSlug}`);
    organizer.value = organizerResponse.data;
    
    // Fetch organizer's events
    const eventsResponse = await axios.get(`/api/organizers/${organizerSlug}/events`);
    events.value = eventsResponse.data;
    
  } catch (err) {
    console.error('Error fetching organizer data:', err);
    if (err.response?.status === 404) {
      error.value = 'Organisatören hittades inte.';
    } else {
      error.value = 'Kunde inte ladda organisatörsdata. Försök igen senare.';
    }
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchOrganizerData();
});
</script>

<style scoped>
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style> 